<script lang="ts" setup>
import { ref, reactive, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import messages from './locales/messages';

const { t } = useI18n({
    useScope: 'global',
    messages
});

const showModal = ref(false);
const step = ref(1); // Pour passer de l'écran 1 à 2

const validate_all = ref(false);

// Liens vers les politiques de confidentialité
const links = {
  ga: 'https://policies.google.com/privacy',
  ads: 'https://policies.google.com/privacy',
  fb: 'https://www.facebook.com/policy.php'
};

// Consentements par service
const consent = reactive({
  essential: true,
  analytics_identified: false, // Google Analytics
  marketing_google: false,     // Google Ads
  marketing_facebook: false    // Facebook Pixel
});

watch(() => consent, () => {
  validate_all.value = consent.analytics_identified && consent.marketing_google && consent.marketing_facebook;
}, { deep: true });

function switchValidateAll() {
  validate_all.value = !validate_all.value;
  consent.analytics_identified = validate_all.value;
  consent.marketing_google = validate_all.value;
  consent.marketing_facebook = validate_all.value;
}
// Query param pour désactiver la modale
function getQueryParam(name) {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
}

function isLikelyBot() {
  const ua = navigator.userAgent;
  return /bot|crawl|slurp|spider|facebook|twitter|pinterest|linkedin/i.test(ua);
}


onMounted(() => {
  const stored = localStorage.getItem('cookie_consent');
  const noConsentModal = getQueryParam('noConsentModal');
  const isBot = window.isBot;
  
  if (!stored && noConsentModal !== '1' && !isBot  && !isLikelyBot()) {
    showModal.value = true;
  } else if (stored) {
    window.zokConsent = JSON.parse(stored); // Sync vers GTM
    consent.essential = window.zokConsent.essential;
    consent.analytics_identified = window.zokConsent.analytics_identified;
    consent.marketing_google = window.zokConsent.marketing_google;
    consent.marketing_facebook = window.zokConsent.marketing_facebook;
    saveConsent();
  }
});

// Actions
function acceptAll() {
  const allConsent = {
    essential: true,
    analytics_identified: true,
    marketing_google: true,
    marketing_facebook: true
  };
  consent.analytics_identified = true;
  consent.marketing_google = true;
  consent.marketing_facebook = true;
  saveConsent();
}

function refuseAll() {
  const minimalConsent = {
    essential: true,
    analytics_identified: false,
    marketing_google: false,
    marketing_facebook: false
  };
    consent.analytics_identified = false;
    consent.marketing_google = false;
    consent.marketing_facebook = false;
  saveConsent();
}

function saveConsent() {
  let values = {
    essential: consent.essential,
    analytics_identified: consent.analytics_identified,
    marketing_google: consent.marketing_google,
    marketing_facebook: consent.marketing_facebook
  };
  localStorage.setItem('cookie_consent', JSON.stringify(values));
  window.zokConsent = values;
  // Update GTM Consent Mode
  window.dataLayer = window.dataLayer || [];
  
  
  const gtmConsentUpdate = {
    'event': 'consentUpdated',
    'consent_ad_storage': values.marketing_google || values.marketing_facebook ? 'granted' : 'denied',
    'consent_analytics_storage': values.analytics_identified ? 'granted' : 'denied',
    'consent_functionality_storage': 'granted',
    'consent_security_storage': 'granted',
    'consent_facebook_pixel': values.marketing_facebook ? 'granted' : 'denied',
    
  };

  // Send the consent update to GTM
  window.dataLayer.push(gtmConsentUpdate);
  
  //window.dataLayer.push({ event: 'consentUpdated' });
  showModal.value = false;
  step.value = 1;
  
}
</script>
<template>
      <!-- Lien Footer -->
      <a @click.prevent="showModal = true">{{ $t('cookie_consent.manage') }}</a>
  
      <!-- Modal -->
      <v-dialog v-model="showModal" persistent max-width="600px">
        <v-card>
            <v-card-title class="text-h6">
                <template v-if="step === 1">
                    {{ $t('cookie_consent.title') }}
                </template>
                <template v-else>
                    <v-icon>mdi-cookie</v-icon>{{ $t('cookie_consent.title2') }}
                </template>
            </v-card-title>
  
          <v-card-text>
            <!-- Écran 1 : Message simple -->
            <div v-if="step === 1" class="text-caption position-relative">
                <img src="@/assets/logos/logo_kase_FR_Rouge_Desktop.png" alt="KaseFilter.fr" class="w-20 mx-auto mb-3">
              <p v-html="$t('cookie_consent.description').replaceAll('\n','<br />')"></p>
              <ol class="ml-10 mt-2 mb-3">
                <li>{{ $t('cookie_consent.cookies_type.essential') }}</li>
                <li>{{ $t('cookie_consent.cookies_type.analytics') }}</li>
                <li>{{ $t('cookie_consent.cookies_type.improvement') }}</li>
                <li>{{ $t('cookie_consent.cookies_type.measure') }}</li>
                <li>{{ $t('cookie_consent.cookies_type.ads') }}</li>
                <li>{{ $t('cookie_consent.cookies_type.personalization') }}</li>
              </ol>
              <p>{{ $t('cookie_consent.description2') }}</p>
            </div>
  
            <!-- Écran 2 : Personnalisation -->
            <div v-else>
                <v-row align="center">
                    <v-col>
                        <label for="all" @click="switchValidateAll" class="d-flex align-center">
                            <v-icon>mdi-cookie</v-icon> {{  $t('cookie_consent.switch_all') }}
                        </label>
                    </v-col>
                    <v-col cols="2">
                        <v-switch name="all" @click="switchValidateAll" v-model="validate_all" color="primary" />
                    </v-col>
                </v-row>
                
                <hr />
                <v-row align="center" class="mb-2">
                    <v-col>
                        <label for="analytics" class="d-flex align-center" @click="consent.analytics_identified = !consent.analytics_identified">
                            <v-icon>mdi-google-analytics</v-icon>
                            {{  $t('cookie_consent.ga') }}
                        </label>
                        <small><a :href="links.ga" target="_blank">{{ $t('cookie_consent.policy') }}</a></small>
                    </v-col>
                    <v-col cols="2">
                        <v-switch v-model="consent.analytics_identified" color="primary" />
                    </v-col>
                </v-row>

                <v-row align="center" class="mb-2">
                    <v-col>
                        <label for="google" class="d-flex align-center" @click="consent.marketing_google = !consent.marketing_google">
                            <v-icon>mdi-google-ads</v-icon>
                            {{  $t('cookie_consent.ads') }}
                        </label>
                        <small><a :href="links.ads" target="_blank">{{ $t('cookie_consent.policy') }}</a></small>
                    </v-col>
                    <v-col cols="2">
                        <v-switch v-model="consent.marketing_google" color="primary" />
                    </v-col>
                    
                </v-row>

                <v-row align="center" class="mb-2">
                    <v-col>
                        <label for="facebook" class="d-flex align-center" @click="consent.marketing_facebook = !consent.marketing_facebook">
                            <v-icon>mdi-facebook</v-icon>
                            {{  $t('cookie_consent.fb') }}
                        </label>
                        <small><a :href="links.fb" target="_blank">{{ $t('cookie_consent.policy') }}</a></small>
                    </v-col>
                    <v-col cols="2">
                        <v-switch v-model="consent.marketing_facebook" color="primary" />
                    </v-col>
                </v-row>
            </div>
          </v-card-text>
  
          <v-card-actions>
            <!-- Boutons selon l'étape -->
            <template v-if="step === 1">
              <v-btn color="secondary" @click="step = 2">{{ $t('cookie_consent.customize') }}</v-btn>
              <v-btn color="primary" variant="tonal" @click="acceptAll">{{ $t('cookie_consent.accept_all') }}</v-btn>
            </template>
  
            <template v-else>
              <v-btn color="primary" @click="acceptAll">{{ $t('cookie_consent.accept_all') }}</v-btn>
              <v-btn color="secondary" @click="refuseAll">{{ $t('cookie_consent.refuse_all') }}</v-btn>
              <v-spacer></v-spacer>
              <v-btn color="success" @click="saveConsent">{{ $t('cookie_consent.save') }}</v-btn>
            </template>
          </v-card-actions>
        </v-card>
      </v-dialog>
    
  </template>